import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import UserDropdown from '../UserDropdown';

const RiderRideHistory = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, cancelled

  useEffect(() => {
    fetchRideHistory();
  }, []);

  const fetchRideHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/riders/rides/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRides(response.data || []);
    } catch (error) {
      console.error('Error fetching ride history:', error);
      setRides([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTip = (ride) => {
    // Navigate to dedicated tip page
    if (ride.is_day_booking) {
      navigate(`/day-bookings/${ride.booking_id}/tip`);
    } else {
      navigate(`/rides/${ride.id}/tip`);
    }
  };

  const canStillTip = (ride) => {
    // V2/V3 UNIFIED PAYMENT FLOW: Check tip_window_expires_at
    if (ride.payment_flow_version >= 2 && ride.tip_window_expires_at) {
      // Already submitted or declined
      if (ride.tip_submitted_at || ride.tip_declined || ride.payment_captured) {
        return false;
      }

      const timeStr = String(ride.tip_window_expires_at).endsWith('Z')
        ? ride.tip_window_expires_at
        : ride.tip_window_expires_at + 'Z';
      const expiresAt = new Date(timeStr);
      const now = new Date();

      console.log(`[canStillTip V2/V3] Ride ${ride.id}:`, {
        tip_window_expires_at: ride.tip_window_expires_at,
        expiresAt: expiresAt.toISOString(),
        now: now.toISOString(),
        canTip: now < expiresAt
      });

      return now < expiresAt;
    }

    // V1 FLOW: For day bookings, check rider_auto_completed flag
    if (ride.is_day_booking) {
      if (!ride.rider_auto_completed) {
        // For day bookings that weren't auto-completed, no tip window
        return false;
      }

      // Check 24-hour window from dropoff time
      const dropoffTimeStr = ride.estimated_dropoff_time || ride.dropoff_datetime;
      if (!dropoffTimeStr) {
        console.log(`[canStillTip] Day Booking ${ride.id}: No dropoff time found`);
        return false;
      }

      const timeStr = String(dropoffTimeStr).endsWith('Z') ? dropoffTimeStr : dropoffTimeStr + 'Z';
      const dropoffTime = new Date(timeStr);
      const now = new Date();
      const hoursSinceDropoff = (now - dropoffTime) / (1000 * 60 * 60);

      console.log(`[canStillTip] Day Booking ${ride.id}:`, {
        rider_auto_completed: ride.rider_auto_completed,
        dropoffTimeStr,
        hoursSinceDropoff,
        canTip: hoursSinceDropoff < 24
      });

      return hoursSinceDropoff < 24;
    }

    // V1: For regular rides, check if autocompleted and within 24 hours from estimated dropoff time
    if (ride.rider_auto_completed) {
      // Use estimated_dropoff_time or dropoff_datetime (backend alias)
      const dropoffTimeStr = ride.estimated_dropoff_time || ride.dropoff_datetime;
      if (!dropoffTimeStr) {
        console.log(`[canStillTip] Ride ${ride.id}: No dropoff time found`);
        return false;
      }

      // Parse as UTC by appending 'Z' if not already present
      const timeStr = String(dropoffTimeStr).endsWith('Z') ? dropoffTimeStr : dropoffTimeStr + 'Z';
      const dropoffTime = new Date(timeStr);
      const now = new Date();
      const hoursSinceDropoff = (now - dropoffTime) / (1000 * 60 * 60);

      console.log(`[canStillTip] Ride ${ride.id}:`, {
        rider_auto_completed: ride.rider_auto_completed,
        dropoffTimeStr,
        dropoffTime: dropoffTime.toISOString(),
        now: now.toISOString(),
        hoursSinceDropoff,
        canTip: hoursSinceDropoff < 24
      });

      return hoursSinceDropoff < 24;
    }

    // For manually completed rides, they were already prompted for tip, so don't show tip button
    return false;
  };

  // Calculate time remaining for V2 tip window
  const getTipWindowTimeRemaining = (ride) => {
    if (!ride.tip_window_expires_at) return null;

    const timeStr = String(ride.tip_window_expires_at).endsWith('Z')
      ? ride.tip_window_expires_at
      : ride.tip_window_expires_at + 'Z';
    const expiresAt = new Date(timeStr);
    const now = new Date();
    const msRemaining = expiresAt - now;

    if (msRemaining <= 0) return null;

    const hours = Math.floor(msRemaining / (1000 * 60 * 60));
    const minutes = Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
  };

  const formatDateTime = (datetime) => {
    return new Date(datetime).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredRides = rides.filter(ride => {
    if (filter === 'all') return true;
    if (filter === 'completed') return ride.status === 'completed';
    if (filter === 'cancelled') return ride.status === 'cancelled_by_rider' || ride.status === 'cancelled_by_driver';
    return true;
  });

  const getRideStatusBadge = (status) => {
    const badges = {
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-blue-100 text-blue-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading ride history...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/rider/dashboard')}
                className="text-gray-600 hover:text-gray-900"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Ride History</h1>
            </div>
            <UserDropdown user={user} onLogout={handleLogout} />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filter Buttons */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Rides ({rides.length})
            </button>
            <button
              onClick={() => setFilter('completed')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'completed'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completed ({rides.filter(r => r.status === 'completed').length})
            </button>
            <button
              onClick={() => setFilter('cancelled')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'cancelled'
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Cancelled ({rides.filter(r => r.status === 'cancelled_by_rider' || r.status === 'cancelled_by_driver').length})
            </button>
          </div>
        </div>

        {/* Rides List */}
        {filteredRides.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <p className="text-gray-500 text-lg">No {filter !== 'all' ? filter : ''} rides found</p>
            <button
              onClick={() => navigate('/rider/browse-drivers')}
              className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Book a Ride
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRides.map(ride => (
              <div key={ride.id} className="bg-white rounded-lg shadow hover:shadow-md transition p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRideStatusBadge(ride.status)}`}>
                        {ride.status === 'auto_completed' ? 'Completed' : ride.status.charAt(0).toUpperCase() + ride.status.slice(1)}
                      </span>
                      {ride.is_day_booking && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded font-medium">
                          {ride.rate_type === 'half_day' ? 'Half-Day' : 'Full-Day'} ({ride.leg_count} {ride.leg_count === 1 ? 'trip' : 'trips'})
                        </span>
                      )}
                      {!ride.is_day_booking && (
                        <span className="text-sm text-gray-500">
                          Ride #{ride.id}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      Driver: {ride.driver_name || 'N/A'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {formatDateTime(ride.pickup_datetime)}
                    </p>
                  </div>
                  <div className="text-right">
                    {(() => {
                      const fare = parseFloat(ride.total_fare || ride.flat_rate || ride.final_price || 0);
                      const tip = parseFloat(ride.tip_amount || 0);
                      // Stripe pass-through formula: (fare + $0.30) / 0.971 for fares, tip / 0.971 for tips
                      const fareGross = (fare + 0.30) / 0.971;
                      const tipGross = tip > 0 ? tip / 0.971 : 0;
                      const processingFee = (fareGross - fare) + (tipGross - tip);
                      const total = fareGross + tipGross;

                      return (
                        <>
                          <div className="text-2xl font-bold text-gray-900">
                            ${total.toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Total Amount Paid</div>
                          <div className="text-xs text-gray-400 mt-2 text-left space-y-0.5">
                            <div>Fare: ${fare.toFixed(2)}</div>
                            {tip > 0 && <div>Tip: ${tip.toFixed(2)}</div>}
                            <div>Stripe Fees: ${processingFee.toFixed(2)}</div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  {ride.is_day_booking ? (
                    /* Day booking: show start/end times and total miles */
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Start Time</p>
                        <p className="text-sm text-gray-600">{formatDateTime(ride.pickup_datetime)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">End Time</p>
                        <p className="text-sm text-gray-600">{ride.dropoff_datetime ? formatDateTime(ride.dropoff_datetime) : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Total Distance</p>
                        <p className="text-sm text-gray-600">{parseFloat(ride.distance_miles || 0).toFixed(1)} miles</p>
                      </div>
                    </div>
                  ) : (
                    /* Regular ride: show pickup/dropoff addresses */
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Pickup</p>
                          <p className="text-sm text-gray-600">{ride.pickup_address || ride.pickup_location}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Dropoff</p>
                          <p className="text-sm text-gray-600">{ride.dropoff_address || ride.dropoff_location}</p>
                        </div>
                      </div>

                      {ride.distance_miles && (
                        <div className="mt-3 text-sm text-gray-600">
                          Distance: {parseFloat(ride.distance_miles).toFixed(1)} miles
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Add Tip Button - navigates to dedicated tip page */}
                {/* Show for completed rides OR rides where rider has completed (even if driver hasn't) */}
                {(ride.status === 'completed' || ride.status === 'auto_completed' || ride.rider_status === 'completed') &&
                  canStillTip(ride) &&
                  (!ride.tip_amount || ride.tip_amount === 0) && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <button
                      onClick={() => handleAddTip(ride)}
                      className="w-full py-3 px-4 rounded-lg font-medium transition bg-blue-500 text-white hover:bg-blue-600 flex items-center justify-center gap-2"
                    >
                      <span>Add Tip</span>
                      <span className="text-sm opacity-80">({getTipWindowTimeRemaining(ride)})</span>
                    </button>
                  </div>
                )}

                {/* Tip Window Expired */}
                {(ride.status === 'completed' || ride.rider_status === 'completed') &&
                  (ride.rider_auto_completed || ride.payment_flow_version >= 2) &&
                  !canStillTip(ride) &&
                  (!ride.tip_amount || ride.tip_amount === 0) &&
                  !ride.tip_declined && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <p className="text-sm text-gray-500 text-center italic">
                      Tip window expired (24 hours from completion)
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default RiderRideHistory;
